import { emitKeypressEvents } from 'node:readline';
import { spawnSync } from 'node:child_process';
import { writeFileSync, readFileSync, unlinkSync } from 'node:fs';
import { tmpdir } from 'node:os';
import { join } from 'node:path';
import { applyBidi, appBidi } from './bidi';
import { wrapAnsi } from './markdown';
import { keyInput } from './stdinSource';

/**
 * A dependency-free raw-mode line editor with a LIVE autocomplete menu — the Claude-Code DX:
 * the moment you type `/` or `@`, suggestions appear under the prompt and filter as you type;
 * ↑/↓ select, Tab/Enter accept, Esc dismisses. Falls back to a plain read when stdin isn't a TTY.
 *
 * The state machine (EditorState + applyKey) is pure and unit-tested; createLineEditor wires it to
 * stdin keypress events + an ANSI renderer. The REPL keeps raw mode on for its whole session and
 * swaps keypress handlers between "reading a line" (this) and "running a turn" (Esc-to-cancel).
 */

/** A suggestion provider: given the line up to the cursor, return full-replacement hits + the token they replace. */
export type Suggest = (lineToCursor: string) => { hits: string[]; token: string; describe?: (hit: string) => string | undefined };

/** Classify a pasted blob (e.g. a drag-dropped file path) into an attachment: `display` is shown in the
 *  in-buffer placeholder, `ref` is what the placeholder expands to on submit (e.g. an `@/abs/path` mention). */
export type PasteClassifier = (text: string) => { display: string; ref: string } | null;

export interface KeyEvent { name?: string; ctrl?: boolean; meta?: boolean; shift?: boolean; sequence?: string; }

/** Terminal column width of one code point: 2 for East-Asian wide/emoji, 0 for joiners/combining marks. */
export const cpWidth = (cp: number): number => {
  if (cp === 0x200d || cp === 0xfe0f || (cp >= 0x0300 && cp <= 0x036f)) return 0; // ZWJ / VS16 / combining
  return (cp >= 0x1100 && (cp <= 0x115f || cp === 0x2329 || cp === 0x232a ||
    (cp >= 0x2e80 && cp <= 0xa4cf && cp !== 0x303f) ||
    (cp >= 0xac00 && cp <= 0xd7a3) || (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) || (cp >= 0xff00 && cp <= 0xff60) ||
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    (cp >= 0x1f000 && cp <= 0x1faff) || (cp >= 0x20000 && cp <= 0x3fffd))) ? 2 : 1;
};

/** Visible width of a string in terminal COLUMNS (ANSI SGR codes occupy none; CJK/emoji occupy two). */
export const visibleWidth = (s: string): number => {
  let w = 0;
  for (const ch of s.replace(/\x1b\[[0-9;]*m/g, '')) w += cpWidth(ch.codePointAt(0)!);
  return w;
};

/** A typed printable character (incl. Hebrew/Unicode): non-empty and free of control bytes.
 *  Rejects escape sequences (arrow keys etc. arrive as multi-char `str` containing \x1b/control bytes). */
export const isPrintable = (str?: string): str is string => !!str && !/[\x00-\x1f\x7f]/.test(str);

export class EditorState {
  buf = '';
  cursor = 0;            // index into buf
  hits: string[] = [];
  token = '';
  sel = 0;               // highlighted hit
  menuOpen = false;
  prevEsc = false;       // last key was Esc on an empty buffer → next Esc triggers double-Esc (jump-back)
  describe?: (hit: string) => string | undefined;
  private histIdx = -1;  // -1 = editing fresh; ≥0 = browsing history[histIdx]
  private stash = '';     // the fresh buffer, saved while browsing history
  // Bracketed paste: content between ESC[200~/ESC[201~ is buffered as ONE unit, then either typed
  // verbatim (small, single-line) or collapsed to a `[Pasted text …]` placeholder that expands on submit.
  pasting = false;
  private pasteBuf = '';
  private pasteSeq = 0;
  readonly pastes = new Map<string, string>(); // placeholder → full pasted content

  // Vim modal editing (opt-in): undefined = emacs/readline keymap; else 'insert'|'normal'.
  vim?: 'insert' | 'normal';
  vimOp?: 'd' | 'c';   // pending operator awaiting a motion (dd/dw/cc)

  constructor(
    private suggest: Suggest,
    private history: string[] = [],
    private classifyPaste?: PasteClassifier,
    /** Called when a paste arrives with NO text — the image-Cmd-V signature (terminal fires an empty paste). */
    private onEmptyPaste?: () => { display: string; ref: string } | null,
    vimEnabled = false,
  ) { if (vimEnabled) this.vim = 'insert'; }

  /** The most-recent history entry that EXTENDS the current buffer — the inline ghost-suggestion suffix.
   *  Empty unless the buffer is a single non-empty line with the cursor at its end and no menu/search open. */
  ghost(): string {
    if (!this.buf || this.cursor !== this.buf.length || this.menuOpen || this.searching || this.buf.includes('\n')) return '';
    for (const h of this.history) if (h.length > this.buf.length && h.startsWith(this.buf)) return h.slice(this.buf.length);
    return '';
  }
  /** Accept the ghost suggestion (append its remainder). Returns false when there's nothing to accept. */
  acceptGhost(): boolean { const g = this.ghost(); if (!g) return false; this.insert(g); return true; }

  /** Collapse pastes that would break the single-line editor (multi-line) or flood it (≥ this many chars). */
  static readonly PASTE_INLINE_MAX = 800;

  beginPaste(): void { this.pasting = true; this.pasteBuf = ''; }
  /** Accumulate one character of an in-progress paste (no render/refresh per char — that's the whole point). */
  pasteChar(c: string): void { this.pasteBuf += c; }
  /** Actively grab a clipboard image and attach it (Ctrl-V; same hook as the empty-paste path). */
  grabClipboard(): boolean { const att = this.onEmptyPaste?.(); if (att) this.attach(att.display, att.ref); return !!att; }
  endPaste(): void {
    this.pasting = false;
    const text = this.pasteBuf;
    this.pasteBuf = '';
    // Empty paste = Cmd-V of an IMAGE (the terminal fires a paste event but has no text to send) → grab the clipboard image.
    if (!text) {
      const att = this.onEmptyPaste?.();
      if (att) this.attach(att.display, att.ref);
      return;
    }
    // Drag-dropped / pasted file path → attach (image/file). High-signal because it's a paste, not typed.
    const cls = this.classifyPaste?.(text);
    if (cls) { this.attach(cls.display, cls.ref); return; }
    const multiline = text.includes('\n');
    if (multiline || text.length > EditorState.PASTE_INLINE_MAX) {
      const lines = text.split('\n').length;
      const id = ++this.pasteSeq;
      const measure = multiline ? `+${lines} line${lines === 1 ? '' : 's'}` : `+${text.length} chars`;
      const placeholder = `[Pasted text #${id} ${measure}]`;
      this.pastes.set(placeholder, text);
      this.insert(placeholder);          // a single safe, single-line token the user sees + can delete
    } else {
      this.insert(text);                 // small single-line paste: just type it in
    }
  }
  /** Insert an attachment placeholder (`[Image #N]` / `[File … #N]`) that expands to `ref` on submit. */
  attach(display: string, ref: string): void {
    const placeholder = `[${display} #${++this.pasteSeq}]`;
    this.pastes.set(placeholder, ref); // expands to an @ref the existing pipeline attaches/inlines
    this.insert(placeholder);
  }
  /** The line as the model should see it: paste placeholders expanded back to their full content. */
  expand(): string {
    let out = this.buf;
    for (const [ph, full] of this.pastes) if (out.includes(ph)) out = out.split(ph).join(full);
    return out;
  }

  /** Recompute suggestions from the line up to the cursor. */
  refresh(): void {
    const r = this.suggest(this.buf.slice(0, this.cursor));
    this.hits = r.hits;
    this.token = r.token;
    this.describe = r.describe;
    // Open the menu when there's something to choose — but not when the sole hit is already typed
    // in full (nothing left to complete), so Enter submits instead of re-accepting. EXCEPTION:
    // a fully-typed slash command keeps its row visible as confirmation it exists (CC parity) —
    // safe because Enter on an exact slash match accepts (a no-op) and submits in one press.
    const exact = r.hits.length === 1 && r.hits[0] === r.token;
    this.menuOpen = r.hits.length > 0 && (!exact || r.token.startsWith('/'));
    if (this.sel >= r.hits.length) this.sel = 0;
  }

  // ── Editor undo (Ctrl-_): snapshot before every mutation, pop to restore ──
  private undoStack: { buf: string; cursor: number }[] = [];
  private snapshot(): void {
    const top = this.undoStack[this.undoStack.length - 1];
    if (top?.buf === this.buf) return; // coalesce no-op states (e.g. cursor-only moves)
    this.undoStack.push({ buf: this.buf, cursor: this.cursor });
    if (this.undoStack.length > 200) this.undoStack.shift();
  }
  undo(): void {
    const p = this.undoStack.pop();
    if (!p) return;
    this.buf = p.buf;
    this.cursor = Math.min(p.cursor, p.buf.length);
    this.histIdx = -1;
    this.refresh();
  }

  // Code-point boundaries: cursor/delete ops must never split a surrogate pair (emoji = 2 code units;
  // a half-pair in the buffer reaches the model as mojibake). Steps are by code POINT (lean — ZWJ
  // families delete component-wise, which even many terminals do).
  private prevCp(i: number): number {
    if (i >= 2 && /[\uDC00-\uDFFF]/.test(this.buf[i - 1]) && /[\uD800-\uDBFF]/.test(this.buf[i - 2])) return i - 2;
    return Math.max(0, i - 1);
  }
  private nextCp(i: number): number {
    if (i <= this.buf.length - 2 && /[\uD800-\uDBFF]/.test(this.buf[i]) && /[\uDC00-\uDFFF]/.test(this.buf[i + 1])) return i + 2;
    return Math.min(this.buf.length, i + 1);
  }

  insert(s: string): void {
    this.snapshot();
    this.buf = this.buf.slice(0, this.cursor) + s + this.buf.slice(this.cursor);
    this.cursor += s.length;
    this.histIdx = -1;
    this.refresh();
  }
  backspace(): void {
    if (this.cursor === 0) return;
    this.snapshot();
    const start = this.prevCp(this.cursor);
    this.buf = this.buf.slice(0, start) + this.buf.slice(this.cursor);
    this.cursor = start;
    this.histIdx = -1;
    this.refresh();
  }
  del(): void {
    if (this.cursor >= this.buf.length) return;
    this.snapshot();
    this.buf = this.buf.slice(0, this.cursor) + this.buf.slice(this.nextCp(this.cursor));
    this.refresh();
  }
  left(): void { if (this.cursor > 0) this.cursor = this.prevCp(this.cursor); }
  right(): void { if (this.cursor < this.buf.length) this.cursor = this.nextCp(this.cursor); }
  home(): void { this.cursor = 0; }
  end(): void { this.cursor = this.buf.length; }

  // ── Word motion + kill ring (emacs/readline parity: Ctrl-W/U/K, Alt-B/F/D, Alt/Ctrl-arrows, Ctrl-Y) ──
  killed = '';                                              // last-killed text, re-inserted by Ctrl-Y (yank)
  private isWordChar(i: number): boolean { return i >= 0 && i < this.buf.length && !/\s/.test(this.buf[i]); }
  /** Start of the whitespace-delimited word at/just before `i` (skip spaces, then the word). */
  private prevWord(i: number): number { let j = i; while (j > 0 && !this.isWordChar(j - 1)) j--; while (j > 0 && this.isWordChar(j - 1)) j--; return j; }
  /** End of the whitespace-delimited word at/just after `i` (skip spaces, then the word). */
  private nextWord(i: number): number { let j = i; while (j < this.buf.length && !this.isWordChar(j)) j++; while (j < this.buf.length && this.isWordChar(j)) j++; return j; }
  private cut(from: number, to: number): void { this.snapshot(); this.killed = this.buf.slice(from, to); this.buf = this.buf.slice(0, from) + this.buf.slice(to); this.cursor = from; this.histIdx = -1; this.refresh(); }

  wordLeft(): void { this.cursor = this.prevWord(this.cursor); }
  wordRight(): void { this.cursor = this.nextWord(this.cursor); }
  killWordBack(): void { if (this.cursor > 0) this.cut(this.prevWord(this.cursor), this.cursor); }              // Ctrl-W / Alt-⌫
  killWordForward(): void { const e = this.nextWord(this.cursor); if (e > this.cursor) { const at = this.cursor; this.cut(this.cursor, e); this.cursor = at; } } // Alt-D
  killToStart(): void { if (this.cursor > 0) this.cut(0, this.cursor); }                                        // Ctrl-U
  killToEnd(): void { if (this.cursor < this.buf.length) { const at = this.cursor; this.cut(this.cursor, this.buf.length); this.cursor = at; } } // Ctrl-K
  yank(): void { if (this.killed) this.insert(this.killed); }                                                   // Ctrl-Y

  // ── Reverse incremental history search (Ctrl-R) ──
  searching = false;
  searchQuery = '';
  searchMiss = false;          // current query matches nothing (render shows "failing")
  private searchStash = '';    // buffer to restore if the search is cancelled
  private searchFrom = 0;      // history index the next match scans from
  startSearch(): void { this.searching = true; this.searchQuery = ''; this.searchMiss = false; this.searchStash = this.buf; this.searchFrom = 0; this.closeMenu(); this.locate(); }
  /** Load the first history entry at/after `searchFrom` containing the query into the buffer (a live preview). */
  private locate(): void {
    const q = this.searchQuery.toLowerCase();
    for (let i = this.searchFrom; i < this.history.length; i++) {
      if (this.history[i].toLowerCase().includes(q)) { this.buf = this.history[i]; this.cursor = this.buf.length; this.searchFrom = i; this.searchMiss = false; return; }
    }
    this.searchMiss = this.searchQuery.length > 0; // nothing older matched — keep showing the last hit
  }
  searchType(c: string): void { this.searchQuery += c; this.searchFrom = 0; this.locate(); }
  searchBack(): void { this.searchQuery = this.searchQuery.slice(0, -1); this.searchFrom = 0; this.locate(); }
  searchOlder(): void { this.searchFrom += 1; this.locate(); } // Ctrl-R again → next older match
  endSearch(accept: boolean): void {
    this.searching = false; this.searchQuery = ''; this.searchMiss = false;
    if (!accept) { this.buf = this.searchStash; this.cursor = this.buf.length; } // cancel → restore the pre-search line
    this.histIdx = -1; this.refresh();
  }

  menuUp(): void { if (this.menuOpen && this.hits.length) this.sel = (this.sel - 1 + this.hits.length) % this.hits.length; }
  menuDown(): void { if (this.menuOpen && this.hits.length) this.sel = (this.sel + 1) % this.hits.length; }
  closeMenu(): void { this.menuOpen = false; this.hits = []; this.sel = 0; }

  /** Replace the trailing token with the highlighted hit; re-suggest (e.g. `@src/` opens its children). */
  accept(): boolean {
    if (!this.menuOpen || !this.hits.length) return false;
    const hit = this.hits[this.sel] ?? this.hits[0];
    const start = this.cursor - this.token.length;
    this.buf = this.buf.slice(0, start) + hit + this.buf.slice(this.cursor);
    this.cursor = start + hit.length;
    this.refresh();
    return true;
  }

  historyPrev(): void { // older
    if (!this.history.length) return;
    if (this.histIdx === -1) this.stash = this.buf;
    this.histIdx = Math.min(this.histIdx + 1, this.history.length - 1);
    this.buf = this.history[this.histIdx] ?? '';
    this.cursor = this.buf.length;
    this.closeMenu();
  }
  historyNext(): void { // newer
    if (this.histIdx === -1) return;
    this.histIdx--;
    this.buf = this.histIdx === -1 ? this.stash : (this.history[this.histIdx] ?? '');
    this.cursor = this.buf.length;
    this.closeMenu();
  }
  reset(): void { this.buf = ''; this.cursor = 0; this.histIdx = -1; this.pasting = false; this.pasteBuf = ''; this.searching = false; this.searchQuery = ''; this.pastes.clear(); this.closeMenu(); this.vimOp = undefined; if (this.vim) this.vim = 'insert'; }
}

/** Sentinel resolved by readLine on double-Esc (empty buffer): the REPL shows a jump-back picker.
 *  A NUL-prefixed string so it can never collide with typed input. */
export const REWIND = '\x00REWIND';

/**
 * Vim NORMAL-mode keymap (baseline subset — CC-style modal editing). Handles motions/edits and returns
 * the REPL action; returns 'pass' to let the caller fall through to the shared keymap (Enter, Ctrl-C, …).
 * Scope (documented, not silently partial): motions h l 0 $ w b e · enter-insert i a A I o ·
 * edits x D C dd dw cc · Esc stays normal. No visual mode, no yank/put register, no counts, no `u` undo.
 */
function applyVimNormal(s: EditorState, key: KeyEvent, str?: string): 'submit' | 'cancel' | 'eof' | 'none' | 'pass' {
  const k = key?.name;
  if (key?.ctrl || key?.meta) return 'pass';                 // control/meta chords use the shared keymap
  if (k === 'return' || k === 'enter') return 'pass';        // submit/newline handled by shared keymap
  if (k === 'escape') { s.vimOp = undefined; return 'pass'; } // Esc → menu dismiss / clear / double-Esc
  const op = s.vimOp; s.vimOp = undefined;
  if (op) {                                                   // operator pending (d/c) → expect a motion
    if (str === 'd' && op === 'd') s.killToStart(), s.killToEnd();      // dd → kill whole line
    else if (str === 'c' && op === 'c') { s.killToStart(); s.killToEnd(); s.vim = 'insert'; } // cc → change line
    else if (str === 'w') {                                  // dw / cw → kill the word + trailing spaces (vim-ish)
      s.killWordForward();
      while (s.buf[s.cursor] === ' ') s.del();
      if (op === 'c') s.vim = 'insert';
    }
    return 'none';
  }
  switch (str) {
    case 'i': s.vim = 'insert'; return 'none';
    case 'a': s.right(); s.vim = 'insert'; return 'none';
    case 'A': s.end(); s.vim = 'insert'; return 'none';
    case 'I': s.home(); s.vim = 'insert'; return 'none';
    case 'o': s.end(); s.insert('\n'); s.vim = 'insert'; return 'none';
    case 'h': s.left(); return 'none';
    case 'l': s.right(); return 'none';
    case '0': s.home(); return 'none';
    case '$': s.end(); return 'none';
    case 'w': case 'e': s.wordRight(); return 'none';
    case 'b': s.wordLeft(); return 'none';
    case 'x': s.del(); return 'none';
    case 'D': s.killToEnd(); return 'none';
    case 'C': s.killToEnd(); s.vim = 'insert'; return 'none';
    case 'd': s.vimOp = 'd'; return 'none';
    case 'c': s.vimOp = 'c'; return 'none';
  }
  if (k === 'left') { s.left(); return 'none'; }
  if (k === 'right') { s.right(); return 'none'; }
  if (k === 'up') { s.historyPrev(); return 'none'; }
  if (k === 'down') { s.historyNext(); return 'none'; }
  if (k === 'backspace') { s.left(); return 'none'; }        // normal-mode backspace just moves left (vim)
  return 'none';                                             // swallow other printables in normal mode
}

/** Apply one keypress to the state. Returns the resulting REPL action. */
export function applyKey(s: EditorState, key: KeyEvent, str?: string): 'submit' | 'cancel' | 'eof' | 'rewind' | 'none' {
  const k = key?.name;
  // Vim normal mode intercepts first (unless mid-paste/search, handled below). 'pass' falls through.
  if (s.vim === 'normal' && !s.pasting && !s.searching) {
    const r = applyVimNormal(s, key ?? {}, str);
    if (r !== 'pass') return r;
  }
  // Bracketed paste: swallow everything between the markers into one unit (Enter is a newline here, not submit).
  if (k === 'paste-start') { s.beginPaste(); return 'none'; }
  if (s.pasting) {
    if (k === 'paste-end') { s.endPaste(); return 'none'; }
    const c = (k === 'enter' || k === 'return') ? '\n' : k === 'tab' ? '\t' : (str ?? '');
    if (c) s.pasteChar(c);
    return 'none';
  }
  // Reverse incremental search (Ctrl-R) captures input until accepted (Enter/arrow) or cancelled (Esc/Ctrl-G/Ctrl-C).
  if (s.searching) {
    if (key?.ctrl && k === 'r') { s.searchOlder(); return 'none'; }   // next older match
    if (key?.ctrl && k === 'c') { s.endSearch(false); return 'cancel'; }
    if ((key?.ctrl && k === 'g') || k === 'escape') { s.endSearch(false); return 'none'; } // cancel → restore line
    if (k === 'return' || k === 'enter') { s.endSearch(true); return 'none'; } // accept → keep editing (Enter again submits)
    if (k === 'backspace') { s.searchBack(); return 'none'; }
    if (!key?.ctrl && !key?.meta && isPrintable(str)) { s.searchType(str); return 'none'; }
    s.endSearch(true); // any other key (arrows, etc.) accepts the match, then falls through to act normally
  }
  if (key?.ctrl && k === 'r') return (s.startSearch(), 'none');
  const wasEsc = s.prevEsc; s.prevEsc = false; // any key clears the double-Esc arming; the escape case re-arms
  if (key?.ctrl && k === 'c') return 'cancel';
  if (key?.ctrl && k === 'd') return s.buf.length === 0 ? 'eof' : (s.del(), 'none');
  if (key?.ctrl && k === 'a') return (s.home(), 'none');     // line start
  if (key?.ctrl && k === 'e') return (s.end(), 'none');      // line end
  if (key?.ctrl && k === 'b') return (s.left(), 'none');     // char back  (emacs)
  if (key?.ctrl && k === 'f') return (s.right(), 'none');    // char fwd   (emacs)
  if (key?.ctrl && k === 'w') return (s.killWordBack(), 'none');    // kill word back
  if (key?.ctrl && k === 'u') return (s.killToStart(), 'none');     // kill to line start
  if (key?.ctrl && k === 'k') return (s.killToEnd(), 'none');       // kill to line end
  if (key?.ctrl && k === 'y') return (s.yank(), 'none');           // yank (paste last kill)
  // Ctrl-V: actively grab a clipboard image (CC parity). The Cmd-V empty-paste path only works on
  // terminals that fire a bracketed paste for an image-only clipboard — many fire nothing at all.
  if (key?.ctrl && k === 'v') { s.grabClipboard(); return 'none'; }
  if (str === '\x1f') return (s.undo(), 'none');                   // Ctrl-_ / Ctrl-7 → undo last edit
  // Alt/Meta word ops (also Ctrl+arrow on most terminals) — word motion + word kill.
  if (key?.meta && k === 'b') return (s.wordLeft(), 'none');
  if (key?.meta && k === 'f') return (s.wordRight(), 'none');
  if (key?.meta && k === 'd') return (s.killWordForward(), 'none');
  if (key?.meta && k === 'backspace') return (s.killWordBack(), 'none');
  if ((key?.meta || key?.ctrl) && k === 'left') return (s.wordLeft(), 'none');
  if ((key?.meta || key?.ctrl) && k === 'right') return (s.wordRight(), 'none');
  // Option/Alt+Enter (meta) or an ESC-prefixed CR/LF → newline, not submit (multi-line authoring, CC parity).
  if ((key?.meta && (k === 'return' || k === 'enter')) || str === '\x1b\r' || str === '\x1b\n') { s.insert('\n'); return 'none'; }
  switch (k) {
    case 'return': case 'enter':
      if (s.menuOpen) {
        const isCmd = s.token.startsWith('/'); // slash-command → accept + run in one press (CC parity)
        s.accept();
        return isCmd ? 'submit' : 'none';      // @paths: accept + keep editing (Enter again submits)
      }
      // `\` immediately before the cursor + Enter → continuation newline (works on every terminal, no meta needed).
      if (s.buf.slice(0, s.cursor).endsWith('\\')) { s.backspace(); s.insert('\n'); return 'none'; }
      return 'submit';
    case 'tab': if (s.menuOpen) { s.accept(); return 'none'; } if (s.acceptGhost()) return 'none'; return 'none'; // menu accept, else accept history ghost
    case 'escape':
      if (s.menuOpen) { s.closeMenu(); return 'none'; } // open menu → dismiss it first
      if (s.vim === 'insert') { s.vim = 'normal'; return 'none'; } // vim: insert → normal (don't clear)
      if (s.vim === 'normal' && s.buf.length) return 'none';       // vim: Esc in normal is a no-op (never clears)
      if (s.buf.length) return 'cancel';                // non-empty line → clear it (Esc never exits the REPL)
      // A FAST double-tap arrives as ONE merged keypress (node holds a lone ESC for escapeCodeTimeout
      // waiting for a CSI tail). meta:true can't discriminate (a lone ESC sets it too) — the sequence can.
      if (wasEsc || key?.sequence === '\x1b\x1b') return 'rewind'; // double-Esc on an empty line → jump-back picker (CC parity)
      s.prevEsc = true; return 'none';                  // first Esc on empty → arm double-Esc
    case 'up': s.menuOpen ? s.menuUp() : s.historyPrev(); return 'none';
    case 'down': s.menuOpen ? s.menuDown() : s.historyNext(); return 'none';
    case 'left': s.left(); return 'none';
    case 'right': if (s.cursor === s.buf.length && s.acceptGhost()) return 'none'; s.right(); return 'none'; // at EOL → accept ghost
    case 'home': s.home(); return 'none';
    case 'end': s.end(); return 'none';
    case 'backspace': s.backspace(); return 'none';
    case 'delete': s.del(); return 'none';
  }
  // printable input (ignore control/meta-prefixed keys)
  if (!key?.ctrl && !key?.meta && isPrintable(str)) { s.insert(str); return 'none'; }
  return 'none';
}

export interface ReadOptions {
  /** May contain ANSI; its visible width sets the cursor column. A function makes the prompt LIVE —
   *  re-evaluated on every repaint (e.g. flip to a '? yes/no' question prompt while an ask is pending;
   *  pair with redrawNow() so the flip paints immediately). */
  prompt: string | (() => string);
  suggest: Suggest;
  history?: string[];      // most-recent-first
  maxVisible?: number;     // menu rows shown at once (default 8)
  classifyPaste?: PasteClassifier; // turn pasted file paths into attachments (optional)
  /** Grab a clipboard image when a paste arrives empty (Cmd-V of an image fires a text-less paste). */
  onEmptyPaste?: () => { display: string; ref: string } | null;
  /** Pre-fill the buffer (e.g. a message recalled by double-Esc jump-back), ready to edit + resend. */
  initial?: string;
  /** Optional dim status footer (e.g. context% · cost) drawn under the input while typing; cleared on submit. */
  status?: () => string;
  /** Re-render the footer every N ms while waiting for input — lets `status()` animate live activity
   *  (e.g. a duplex background task spinner) so a long-running worker never looks like a hang. */
  statusTickMs?: number;
  /** Modal editing: start the line in vim mode (insert, Esc→normal). Default emacs/readline keymap. */
  vimMode?: boolean;
  /** First Esc on an empty idle line — host-level claim (e.g. cancel running background tasks).
   *  Return true if handled (prints its own notice); false falls through to double-Esc arming. */
  onEscapeIdle?: () => boolean;
  /** Shift+Tab → cycle permission posture; returns a label to surface (footer re-renders). */
  onCyclePosture?: () => string;
  /** Alt+T → cycle reasoning effort; returns a label to surface. */
  onToggleThinking?: () => string;
  /** Ctrl+O → toggle verbose output (full tool results vs preview); returns a label to surface. */
  onToggleVerbose?: () => string;
  /** Alt+P → open the model picker inline (buffer preserved); returns the new model id or null. */
  onPickModel?: () => Promise<string | null>;
  /** Called SYNCHRONOUSLY the instant a line submits, before the promise resolves — keys buffered
   *  behind the Enter in the same input burst dispatch after this, so the host can claim them. */
  onSubmit?: () => void;
  /** Ctrl+S with text → push current input to stash queue. */
  onStash?: (text: string) => void;
  /** Ctrl+S on an empty buffer → pop the next stashed input into the editor. */
  onUnstash?: () => string | undefined;
}

export interface LineEditor {
  /** Read one line. Resolves to the text, or null on EOF/Ctrl-D (or Ctrl-C at an empty prompt). */
  readLine(opts: ReadOptions): Promise<string | null>;
  /** Repaint the active prompt block at the CURRENT cursor position. For async writers (background
   *  task results landing mid-prompt): clear your line, print your content, then call this — the
   *  prompt + buffer + footer re-render cleanly below it. No-op when no read is active. */
  redrawNow(): void;
  /** Pause all prompt rendering (streamed stdout owns the terminal); clears the prompt block.
   *  resume() repaints it fresh at the current cursor position. */
  suspend(): void;
  resume(): void;
  /** Externally resolve the pending readLine with null (same effect as Ctrl-D). No-op when idle. */
  abort(): void;
}

/** Build a line editor bound to a TTY output stream (stderr in the REPL). */
export function createLineEditor(out: NodeJS.WriteStream & { columns?: number }): LineEditor {
  const isTTY = !!out.isTTY && !!process.stdin.isTTY;
  const dim = (x: string) => `\x1b[2m${x}\x1b[0m`;
  const inverse = (x: string) => `\x1b[7m${x}\x1b[0m`;
  const COLOR: Record<InputMode['name'], (x: string) => string> = {
    bash: (x) => `\x1b[31m${x}\x1b[0m`,   // red
    memory: (x) => `\x1b[36m${x}\x1b[0m`, // cyan
  };

  let curRow = 0; // rows the terminal cursor sits below the input's first line (multi-line redraws climb back up)
  let suspended = false; // streamed stdout owns the terminal; all renders no-op until resume()
  let activeRedraw: (() => void) | undefined; // set while a readLine is live (see LineEditor.redrawNow)
  let activeAbort: (() => void) | undefined; // set while a readLine is live; called by abort() to resolve with null

  // Multi-line aware: buf may hold real newlines (Opt-Enter / `\`-continuation). The prompt sits on row 0;
  // each further buf line renders at col 0. We climb back to row 0 before clearing, then re-place the cursor.
  function render(s: EditorState, promptArg: string, maxVisible: number, status?: () => string): void {
    if (suspended) return; // a streaming writer owns the terminal — resume() repaints
    const cols = out.columns ?? 80;
    // In reverse-search the prompt becomes the search indicator (bash-style); the matched line follows it.
    // Else, if the buffer opens with a mode prefix (`!` bash / `#` memory), the prompt morphs to that mode's
    // colored indicator live as you type (CC parity) — cleared the instant the prefix is deleted.
    const mode = !s.searching ? inputMode(s.buf) : undefined;
    const vimTag = s.vim === 'normal' && !s.searching && !mode ? inverse(' N ') + ' ' : '';
    const prompt = s.searching
      ? dim(`(${s.searchMiss ? 'failing ' : ''}reverse-i-search)\`${s.searchQuery}': `)
      : mode ? COLOR[mode.name](`${mode.pfx} ${mode.name} › `) : vimTag + promptArg;
    if (curRow > 0) out.write(`\x1b[${curRow}A`); // climb to physical row 0 of the previous draw
    out.write('\r\x1b[J');                         // …then clear it + everything below
    // Hard-wrap into physical rows so a line wider than the terminal can't desync the cursor (we own the
    // wrap points instead of trusting auto-wrap / the magic margin). Row 0 is prefixed by the prompt.
    // In a typed mode the prefix char lives in the indicator, so hide it from the editable buffer view
    // (cursor shifts left by the prefix width to stay aligned). The real s.buf is untouched — submit sees the `!`.
    const rawBuf = mode ? s.buf.slice(mode.pfx.length) : s.buf;
    const rawCursor = mode ? Math.max(0, s.cursor - mode.pfx.length) : s.cursor;
    // Bidi: reorder mixed Hebrew/Arabic+LTR into visual order for the TTY (no-op fast path for pure LTR).
    // OFF unless AGENTX_BIDI=1 — most terminals reorder RTL themselves, so app-side reorder double-flips it.
    const { text: viewBuf, cursor: viewCursor } = appBidi() ? applyBidi(rawBuf, rawCursor) : { text: rawBuf, cursor: rawCursor };
    const { rows, cursorRow, cursorCol } = wrapLayout(visibleWidth(prompt), viewBuf, viewCursor, cols);
    // Inline ghost-suggestion (history autocomplete): drawn dim AFTER the buffer on a single-row line that
    // still has room; the cursor is repositioned to its real column below, so the ghost trails the cursor.
    const ghost = !mode && !s.searching ? s.ghost() : '';
    const ghostFits = ghost && rows.length === 1 && visibleWidth(prompt) + viewBuf.length + ghost.length < cols;
    out.write(prompt + (rows[0] ?? '') + (ghostFits ? dim(ghost) : ''));
    for (let i = 1; i < rows.length; i++) out.write('\r\n' + rows[i]);
    const inputRows = rows.length - 1;
    let menuRows = 0;
    if (s.menuOpen && s.hits.length) {
      const win = windowAround(s.hits.length, s.sel, maxVisible);
      for (let i = win.start; i < win.end; i++) {
        const hit = s.hits[i];
        const d = s.describe?.(hit);
        let label = `  ${hit}${d ? '  ' + d : ''}`;
        if (visibleWidth(label) > cols - 1) label = label.slice(0, cols - 2) + '…'; // keep each label to one physical row
        out.write('\r\n' + (i === s.sel ? inverse(label) : dim(label)));
        menuRows++;
      }
    }
    // Status footer (context/cost) — dim, below everything; multi-line capable (`\n`-separated, e.g. one
    // stacked row per running background task). Never drawn on the commit render (finish passes no
    // `status`), so it doesn't freeze into the scrollback after the line is submitted.
    let footerRows = 0;
    const footer = status?.();
    if (footer) {
      for (const line of footer.split('\n')) {
        const f = visibleWidth(line) > cols - 1 ? line.slice(0, cols - 2) + '…' : line;
        out.write('\r\n' + dim(f));
        footerRows++;
      }
    }
    const up = inputRows + menuRows + footerRows - cursorRow; // climb from the bottom of the draw to the cursor's physical row
    if (up > 0) out.write(`\x1b[${up}A`);
    out.write('\r'); // col 0
    if (cursorCol > 0) out.write(`\x1b[${cursorCol}C`); // → cursor column
    curRow = cursorRow;
  }

  /** Ctrl-X Ctrl-E: hand the buffer to $VISUAL/$EDITOR on a temp file, reload it on a clean exit.
   *  Raw mode + bracketed paste are suspended around the child so the editor owns the terminal. */
  function externalEdit(s: EditorState): void {
    const spec = process.env.VISUAL || process.env.EDITOR || 'vi';
    const [cmd, ...cargs] = spec.split(' ').filter(Boolean); // honor EDITOR="code -w" style values
    const file = join(tmpdir(), `agentx-edit-${process.pid}-${Date.now()}.md`);
    try {
      writeFileSync(file, s.buf);
      process.stdin.setRawMode(false);
      out.write('\x1b[?2004l');
      const r = spawnSync(cmd, [...cargs, file], { stdio: 'inherit' });
      if (r.status === 0) {
        const text = readFileSync(file, 'utf8').replace(/\n$/, '');
        s.reset();
        if (text) s.insert(text);
      }
    } catch { /* editor missing/failed — keep the buffer untouched */ }
    finally {
      try { unlinkSync(file); } catch { /* already gone */ }
      process.stdin.setRawMode(true);
      out.write('\x1b[?2004h');
    }
  }

  async function readLine(opts: ReadOptions): Promise<string | null> {
    const maxVisible = opts.maxVisible ?? 8;
    // Non-TTY (piped/headless): plain line read, no menu.
    if (!isTTY) return readPlainLine();

    const s = new EditorState(opts.suggest, opts.history ?? [], opts.classifyPaste, opts.onEmptyPaste, opts.vimMode);
    curRow = 0; // fresh draw for this read
    if (opts.initial) s.insert(opts.initial); // recalled message — newlines preserved (multi-line editor)
    s.refresh();
    emitKeypressEvents(keyInput);
    keyInput.setRawMode(true);
    keyInput.resume();
    out.write('\x1b[?2004h'); // enable bracketed paste — terminal wraps pasted bytes in ESC[200~ … ESC[201~
    const promptOf = () => (typeof opts.prompt === 'function' ? opts.prompt() : opts.prompt);
    render(s, promptOf(), maxVisible, opts.status);

    // Terminal resized: our cached row count (curRow) is now stale vs the new width. Reset it and redraw —
    // render() re-reads out.columns live, so the new wrap layout is correct; clearing from row 0 avoids garbage.
    const onResize = () => { curRow = 0; render(s, promptOf(), maxVisible, opts.status); };
    // Async interjection support: an external writer cleared its line and printed content — the old
    // draw is gone, so repaint the whole block fresh from wherever the cursor now sits.
    activeRedraw = () => { curRow = 0; render(s, promptOf(), maxVisible, opts.status); };
    process.on('SIGWINCH', onResize);

    return new Promise<string | null>((resolve) => {
      activeAbort = () => { finish(); resolve(null); };
      const redraw = () => render(s, promptOf(), maxVisible, opts.status);
      // Live footer ticker: animates status() (e.g. duplex task spinner) while idle at the prompt.
      // Paused mid-paste (no per-chunk repaints); cleared in finish().
      // Tick re-renders ONLY when the footer text actually changed — a full prompt-block repaint
      // every second reads as flicker (climb + clear + redraw) for a static footer.
      let lastStatus = opts.status?.() ?? '';
      const ticker = opts.statusTickMs && opts.status ? setInterval(() => {
        if (s.pasting) return;
        const cur = opts.status!();
        if (cur === lastStatus) return;
        lastStatus = cur;
        redraw();
      }, opts.statusTickMs) : undefined;
      let chordCtrlX = false; // Ctrl-X arms a readline-style chord (Ctrl-X Ctrl-E → external editor)
      const onKey = (str: string | undefined, key: KeyEvent) => {
        // Ctrl-L: clear the scrollback + screen and redraw the current line at the top (handled here — it writes to the TTY).
        if (key?.ctrl && key.name === 'l') { out.write('\x1b[2J\x1b[3J\x1b[H'); curRow = 0; redraw(); return; }
        // Ctrl-X Ctrl-E: edit the buffer in $VISUAL/$EDITOR (readline parity); any other key disarms.
        // Never arms mid-paste: a literal 0x18 byte inside pasted content must pass through verbatim.
        if (key?.ctrl && key.name === 'x' && !s.pasting) { chordCtrlX = true; return; }
        if (chordCtrlX) {
          chordCtrlX = false;
          if (key?.ctrl && key.name === 'e') { externalEdit(s); curRow = 0; redraw(); return; }
        }
        // Global meta-key shortcuts (CC parity) — act immediately, buffer preserved, no submit:
        if (key?.name === 'tab' && key.shift && opts.onCyclePosture) { opts.onCyclePosture(); redraw(); return; } // Shift+Tab → permission posture
        if (key?.meta && key.name === 't' && opts.onToggleThinking) { opts.onToggleThinking(); redraw(); return; } // Alt+T → reasoning effort
        if (key?.ctrl && key.name === 'o' && opts.onToggleVerbose) { opts.onToggleVerbose(); redraw(); return; } // Ctrl+O → verbose output (CC parity)
        if ((key?.ctrl && key.name === 's') || (key?.meta && key.name === 's')) {                                        // Ctrl+S / Alt+S → stash current / pop stashed
          if (s.buf.trim() && opts.onStash) { opts.onStash(s.expand()); s.reset(); s.refresh(); redraw(); }
          else if (!s.buf.length && opts.onUnstash) { const text = opts.onUnstash(); if (text) { s.insert(text); redraw(); } }
          return;
        }
        // Esc on an empty idle line: let the host claim it first (duplex: cancel running background
        // tasks) — returns true when handled, so it doesn't arm the double-Esc rewind.
        if (key?.name === 'escape' && !s.buf.length && !s.menuOpen && !s.searching && !s.prevEsc && opts.onEscapeIdle?.()) { curRow = 0; redraw(); return; }
        if (key?.meta && key.name === 'p' && opts.onPickModel) {                                                   // Alt+P → model picker (inline)
          keyInput.off('keypress', onKey); // hand stdin to selectMenu, then take it back + redraw in place
          void opts.onPickModel().finally(() => { keyInput.on('keypress', onKey); curRow = 0; redraw(); });
          return;
        }
        const action = applyKey(s, key ?? {}, str);
        if (action === 'submit') { finish(); opts.onSubmit?.(); return resolve(s.expand()); } // expand paste placeholders for the model
        if (action === 'eof') { finish(); return resolve(null); }
        if (action === 'rewind') { finish(); return resolve(REWIND); } // double-Esc → REPL shows the jump-back picker
        if (action === 'cancel') {
          if (s.buf.length === 0) { finish(); return resolve(null); }
          s.reset(); s.refresh(); render(s, promptOf(), maxVisible, opts.status); return; // clear the line, keep going
        }
        if (s.pasting) return; // mid-paste: accumulate silently, then render once on paste-end (no per-char flood)
        render(s, promptOf(), maxVisible, opts.status);
      };
      const finish = () => {
        activeRedraw = undefined;
        activeAbort = undefined;
        if (ticker) clearInterval(ticker);
        keyInput.off('keypress', onKey);
        process.removeListener('SIGWINCH', onResize);
        // bracketed paste stays ON between reads — a paste landing MID-TURN must still arrive as one
        // unit (raw \r fragments would auto-queue as separate turns). The REPL disables it on exit.
        s.closeMenu();
        if (suspended) { out.write('\r\n'); return; } // a stream owns the terminal — no commit paint
        // Commit paint: replace the hard-wrapped editing block with a WORD-wrapped echo for the
        // scrollback. The live editor wraps at the raw column edge (it owns the cursor math); the
        // submitted line shouldn't read mid-word in the transcript forever.
        if (curRow > 0) out.write(`\x1b[${curRow}A`);
        out.write('\r\x1b[J');
        const cols = out.columns ?? 80;
        const mode = inputMode(s.buf);
        const prompt = mode ? COLOR[mode.name](`${mode.pfx} ${mode.name} › `) : promptOf();
        const raw = mode ? s.buf.slice(mode.pfx.length) : s.buf;
        const lines = appBidi() ? raw.split('\n').map((l) => applyBidi(l, 0).text) : raw.split('\n');
        out.write([prompt + lines[0], ...lines.slice(1)].map((l) => wrapAnsi(l, cols, '  ')).join('\r\n') + '\r\n');
        curRow = 0;
      };
      keyInput.on('keypress', onKey);
    });
  }

  return {
    readLine,
    redrawNow: () => activeRedraw?.(),
    suspend: () => {
      if (suspended) return;
      suspended = true;
      if (curRow > 0) out.write(`\x1b[${curRow}A`);
      out.write('\r\x1b[J'); // clear the prompt block — the stream renders into a clean area
      curRow = 0;
    },
    resume: () => {
      if (!suspended) return;
      suspended = false;
      curRow = 0;
      activeRedraw?.();
    },
    abort: () => activeAbort?.(),
  };
}

export interface SelectItem { label: string; value: string; desc?: string; children?: SelectItem[] }
export interface SelectOptions { title?: string; items: SelectItem[]; current?: string; maxVisible?: number; filterable?: boolean }

/**
 * Standalone single-choice picker (raw-mode): renders a scrollable list, ↑/↓ to move, ⏎ to choose,
 * Esc/Ctrl-C to cancel. Items with `children` support drill-down: → expands, ← / Esc collapses.
 * When `filterable`, typing narrows the list (fuzzy match on label+value). Backspace shrinks the filter.
 */
let menusOpen = 0;
/** True while a selectMenu picker owns the keyboard (the in-turn type-ahead handler must yield). */
export const isMenuActive = (): boolean => menusOpen > 0;

export function selectMenu(out: NodeJS.WriteStream & { columns?: number }, opts: SelectOptions): Promise<string | null> {
  if (!out.isTTY || !process.stdin.isTTY || !opts.items.length) return Promise.resolve(null);
  const maxVisible = opts.maxVisible ?? 10;
  const dim = (x: string) => `\x1b[2m${x}\x1b[0m`;
  const inverse = (x: string) => `\x1b[7m${x}\x1b[0m`;
  const cyan = (x: string) => `\x1b[36m${x}\x1b[0m`;
  const yellow = (x: string) => `\x1b[33m${x}\x1b[0m`;

  let items = opts.items;
  let unfiltered = items;  // current level's full list (before filtering)
  let sel = Math.max(0, items.findIndex((i) => i.value === opts.current));
  const stack: { unfiltered: SelectItem[]; sel: number; title?: string; filter: string }[] = [];
  let title = opts.title;
  let filter = '';
  let prevLines = 0;

  const applyFilter = () => {
    if (!filter) { items = unfiltered; }
    else {
      const lc = filter.toLowerCase();
      items = unfiltered.filter((it) => {
        const hay = (it.label + ' ' + it.value + ' ' + (it.desc ?? '')).toLowerCase();
        return lc.split('').every((ch) => hay.includes(ch)) && hay.includes(lc[0]);
      });
    }
    sel = Math.min(sel, Math.max(0, items.length - 1));
  };

  const draw = () => {
    if (prevLines > 1) out.write(`\x1b[${prevLines - 1}A`);
    out.write('\r\x1b[J');
    prevLines = 0;
    const cols = (out.columns ?? 80) - 1;
    const line = (s: string) => (visibleWidth(s) > cols ? s.slice(0, cols - 1) + '…' : s);
    const heading = (title ?? '') + (filter ? '  ' + yellow('/' + filter) : '');
    if (heading) { const rows = heading.split('\n'); out.write(rows.map((h) => dim(line(h))).join('\n') + '\n'); prevLines += rows.length; }
    if (!items.length) {
      out.write(dim('  (no matches)') + '\n'); prevLines++;
    } else {
      const win = windowAround(items.length, sel, maxVisible);
      for (let i = win.start; i < win.end; i++) {
        const it = items[i];
        const arrow = it.children?.length ? cyan(' →') : '';
        const txt = line(` ${it.value === opts.current ? '●' : ' '} ${it.label}${arrow}${it.desc ? '  ' + dim(it.desc) : ''}`);
        out.write((i === sel ? inverse(txt) : txt) + '\n');
        prevLines++;
      }
    }
    const more = items.length > maxVisible ? `  ·  ${sel + 1}/${items.length}` : '';
    const back = stack.length ? '← back · ' : '';
    const filterHint = opts.filterable && !filter ? 'type to filter · ' : '';
    out.write(dim(`  ${filterHint}↑/↓ select · ${back}⏎ choose · esc cancel${more}`));
    prevLines++;
  };

  return new Promise<string | null>((resolve) => {
    const wasRaw = !!keyInput.isRaw;
    menusOpen++;
    emitKeypressEvents(keyInput);
    keyInput.setRawMode(true);
    keyInput.resume();
    draw();
    const finish = (val: string | null) => {
      menusOpen--;
      keyInput.off('keypress', onKey);
      if (prevLines > 1) out.write(`\x1b[${prevLines - 1}A`);
      out.write('\r\x1b[J');
      if (!wasRaw) { try { keyInput.setRawMode(false); } catch { /* not a tty */ } }
      resolve(val);
    };
    const drillIn = () => {
      const it = items[sel];
      if (!it.children?.length) return;
      stack.push({ unfiltered, sel, title, filter });
      title = it.label;
      filter = '';
      const isGroup = it.value.startsWith('__');
      unfiltered = isGroup ? it.children : [{ label: it.label, value: it.value, desc: 'latest' }, ...it.children];
      items = unfiltered;
      sel = Math.max(0, items.findIndex((i) => i.value === opts.current));
      draw();
    };
    const drillOut = () => {
      if (filter) { filter = ''; applyFilter(); draw(); return true; }
      if (!stack.length) return false;
      const prev = stack.pop()!;
      unfiltered = prev.unfiltered; sel = prev.sel; title = prev.title; filter = prev.filter;
      applyFilter();
      draw();
      return true;
    };
    const onKey = (_s: string | undefined, key: KeyEvent) => {
      const k = key?.name;
      if (k === 'up') { sel = (sel - 1 + items.length) % items.length; draw(); }
      else if (k === 'down') { sel = (sel + 1) % items.length; draw(); }
      else if (k === 'right') drillIn();
      else if (k === 'left') drillOut();
      else if (k === 'return' || k === 'enter') { if (items.length) { const it = items[sel]; if (it.children?.length) drillIn(); else finish(it.value); } }
      else if (k === 'escape' || (key?.ctrl && (k === 'c' || k === 'd'))) { if (!drillOut()) finish(null); }
      else if (k === 'backspace' && opts.filterable && filter) { filter = filter.slice(0, -1); applyFilter(); draw(); }
      else if (opts.filterable && !key?.ctrl && !key?.meta && isPrintable(_s)) { filter += _s; applyFilter(); draw(); }
    };
    keyInput.on('keypress', onKey);
  });
}

/**
 * Lay a buffer out into PHYSICAL terminal rows, hard-wrapping any logical line wider than `cols` so the
 * editor controls the wrap points (auto-wrap + the "magic margin" at exactly `cols` would otherwise desync
 * the cursor math). `promptW` = visible columns the prompt occupies on row 0; wrapped/continuation rows start
 * at col 0. Returns the plain-text rows (caller prefixes row 0 with the real prompt) + the cursor's physical
 * (row, col). buf is plain text (no ANSI); `\n` starts a new logical line.
 */
/** A live input mode entered by a buffer prefix (CC parity): `!`→bash, `#`→memory. */
export interface InputMode { pfx: string; name: 'bash' | 'memory' }
const INPUT_MODES: InputMode[] = [
  { pfx: '!', name: 'bash' },
  { pfx: '#', name: 'memory' },
];
/** The input mode a buffer is in by its leading prefix, or undefined for a normal prompt. */
export function inputMode(buf: string): InputMode | undefined {
  return INPUT_MODES.find((m) => buf.startsWith(m.pfx));
}

export function wrapLayout(promptW: number, buf: string, cursor: number, cols: number): { rows: string[]; cursorRow: number; cursorCol: number } {
  const width = Math.max(1, cols);
  const rows: string[] = [];
  let line = '';                 // chars accumulated for the current physical row
  let r = 0, col = promptW;      // current physical row index + column as we lay chars out
  let cursorRow = 0, cursorCol = promptW;
  const pushRow = () => { rows.push(line); line = ''; };
  // Iterate by code point; col advances by DISPLAY width (CJK/emoji = 2) so the cursor math matches
  // what the terminal actually renders. `i` stays a code-unit index (what EditorState.cursor uses).
  for (let i = 0; i <= buf.length;) {
    const cp = i < buf.length ? buf.codePointAt(i)! : -1;
    const ch = cp >= 0 ? String.fromCodePoint(cp) : '';
    const w = ch && ch !== '\n' ? cpWidth(cp) : 0;
    if (w === 2 && col + w > width && col > 0) { pushRow(); r++; col = 0; } // wide char would straddle the edge → wrap BEFORE it (and before placing the cursor on it)
    if (i === cursor) { cursorRow = r; cursorCol = col; } // where char `i` lands = the cursor position
    if (i === buf.length) break;
    if (ch === '\n') { pushRow(); r++; col = 0; i++; continue; }
    line += ch; col += w;
    if (col >= width) { pushRow(); r++; col = 0; } // row full → wrap to the next physical row at col 0
    i += ch.length;
  }
  pushRow(); // final (possibly empty) row
  return { rows, cursorRow, cursorCol };
}

/** Visible window [start,end) of `count` items centered enough to keep `sel` in view. */
export function windowAround(count: number, sel: number, max: number): { start: number; end: number } {
  if (count <= max) return { start: 0, end: count };
  let start = Math.max(0, sel - Math.floor(max / 2));
  if (start + max > count) start = count - max;
  return { start, end: start + max };
}

/** Fallback line read for non-TTY stdin (pipes): one line, resolves null on EOF. */
// Leftover bytes survive across calls so a single piped chunk (`a\nb\nc\n`) yields one line per call
// instead of dropping everything after the first newline. This is what makes pipe-driven e2e work.
let plainBuf = '';
let plainEnded = false;
function readPlainLine(): Promise<string | null> {
  return new Promise((resolve) => {
    const emit = (): boolean => {
      const nl = plainBuf.indexOf('\n');
      if (nl >= 0) { const line = plainBuf.slice(0, nl); plainBuf = plainBuf.slice(nl + 1); resolve(line); return true; }
      if (plainEnded) { const rest = plainBuf; plainBuf = ''; resolve(rest.length ? rest : null); return true; }
      return false;
    };
    const onData = (d: Buffer) => { plainBuf += d.toString('utf8'); if (emit()) cleanup(); };
    const onEnd = () => { plainEnded = true; emit(); cleanup(); };
    const cleanup = () => { process.stdin.off('data', onData); process.stdin.off('end', onEnd); process.stdin.pause(); };
    if (emit()) return; // a full line was already buffered from a prior chunk — no need to read more
    process.stdin.resume();
    process.stdin.on('data', onData);
    process.stdin.on('end', onEnd);
  });
}
